/* eslint-disable no-console */
// MATM cold-boot phase-split collector (MATM-1271 / RN-migration audit rec #3).
//
// The app already sprinkles `window.__matmBootMark?.(name, detail)` calls across
// the hero boot path — the shim, admin-app shell, app-shortcut, MATM App.js, and
// EventRoutes — but NOTHING defines `window.__matmBootMark` in a normal build.
// Only the Playwright boot-optimization harness injects a collector (via
// addInitScript), so today those marks are captured ONLY under the dev-server
// measurement harness. In the field (a prod native build on a real device) every
// call is a no-op and we get zero data.
//
// This module defines the collector for real builds so we capture per-phase
// cold-boot splits from actual devices, then ships ONE compact event into the
// existing PostHog pipeline (reusing the queueing `window.posthog` stub from
// posthogHelper.js — no new infra). That converts the RN go/no-go from taste
// into field data: webview-init / bundle-parse / lazy-chunk-load / auth-RTs /
// first-data-render, measured where it matters.
//
// Design constraints (from the task):
//  - Additive + low-risk. It does NOT change any existing mark call site; it only
//    gives those optional-chained calls something to land on.
//  - Must not add measurable boot cost. The hot path per mark is a single array
//    push. The `enabled` flag is evaluated ONCE at install. All computation +
//    shipping is deferred off the hero path via `later()` after a terminal mark.
//  - Guarded behind a cheap flag. Off by default except prod native builds (the
//    whole point — field numbers), with URL / localStorage overrides for testing.
//  - Never clobbers the Playwright harness: if `window.__matmBootMark` already
//    exists (harness injected it at document-start), we leave it alone.
import AppConfig from 'shared/config-public';
import { Capacitor } from '@capacitor/core';
import { later } from 'shared/utils/later';
import { getBundleAppId } from './getBundleAppId';
import { computeBootPhases, markNow } from './bootPhases';

// The two terminal marks that close a hero-path cold boot. Recording either one
// schedules the (deferred) finalize+ship. See HomePage.js (events hero path) and
// ChatsPage.js (push-tap -> chat thread hero path).
const TERMINAL_MARKS = {
	matm_first_data_render: 'events',
	matm_chats_page_render: 'chats',
	// Business Coach hero paths (BDL perf pass 2026-08-26). Same contract: the
	// mark fires when REAL content is on screen, never on a spinner/skeleton.
	bc_first_data_render: 'dashboard',
	bc_landing_render: 'landing',
};

// Backstop: if the hero path errors/stalls and no terminal mark fires, finalize
// anyway after this long so we still ship whatever splits we captured (tagged
// partial). Deferred, so it never touches the boot hot path.
const FINALIZE_TIMEOUT_MS = 15000;

// MATM is the app this instrumentation targets; the shim/adminapp marks fire for
// every managed app, but we only SHIP a `matm_cold_boot` event for MATM so we
// don't pollute other apps' PostHog with a misnamed event.
const MATM_BUNDLE_ID = 'com.meetatthemirror';
const BC_BUNDLE_ID = 'com.johnoberg.app';

// Which apps ship a cold-boot profile, and under what event name. Keyed by
// bundle app id because that is the only thing that reliably identifies the
// running managed app at finalize() time (see the comment in installBootMark-
// Collector about why the check is deferred).
//
// 🔴 EVENT NAME IS PER-APP ON PURPOSE. Shipping BC's boot under `matm_cold_boot`
// would silently corrupt every MATM boot dashboard with another app's numbers —
// and it would look like a MATM regression, not like a mix-up.
const BOOT_PROFILE_APPS = {
	[MATM_BUNDLE_ID]: 'matm_cold_boot',
	[BC_BUNDLE_ID]: 'bc_cold_boot',
};

// Evaluate the cheap gate exactly once at install. Reads: one URL parse, one
// localStorage get, one Capacitor.isNativePlatform() — all synchronous and
// one-time, so zero per-mark and zero deferred cost when disabled.
function computeEnabled() {
	try {
		const search = new URLSearchParams(window.location.search);
		const qp = search.get('bootprofile');
		if (qp === '1') return true;
		if (qp === '0') return false;
	} catch {
		// URL unavailable (non-browser) — fall through to defaults.
	}

	try {
		const ls =
			window.localStorage?.getItem('boot_profile') ??
			window.localStorage?.getItem('matm_boot_profile');
		if (ls === '1') return true;
		if (ls === '0') return false;
	} catch {
		// localStorage can throw in private mode / sandboxed webviews — ignore.
	}

	// Default: on only for a prod native build (real-device field numbers). Dev
	// servers, web, and non-prod native stay off unless explicitly opted in above.
	try {
		return (
			AppConfig.buildEnv === 'prod' && Capacitor.isNativePlatform()
		);
	} catch {
		return false;
	}
}

// Read the live Navigation Timing context (browser-only), normalized for the
// pure computeBootPhases(). All values are null-safe so the arithmetic degrades
// gracefully when the API is unavailable. domInteractive/domContentLoaded are
// relative to timeOrigin — the same clock as every mark's performance.now()
// `now` — so they're directly comparable.
function readNavContext() {
	let navEntry;
	try {
		[navEntry] = performance.getEntriesByType('navigation');
	} catch {
		navEntry = undefined;
	}

	let domInteractive = navEntry?.domInteractive ?? null;
	if (
		domInteractive == null &&
		typeof performance?.timing?.domInteractive === 'number'
	) {
		// Legacy timing API is absolute epoch ms; rebase onto timeOrigin.
		domInteractive =
			performance.timing.domInteractive - performance.timeOrigin;
	}

	const ttfbMs =
		navEntry &&
		Number.isFinite(navEntry.responseStart - navEntry.requestStart)
			? Math.round(navEntry.responseStart - navEntry.requestStart)
			: null;

	return {
		domInteractive,
		ttfbMs,
		domContentLoadedMs: navEntry?.domContentLoadedEventEnd ?? null,
	};
}

export function installBootMarkCollector() {
	if (typeof window === 'undefined') return;

	// The Playwright boot-optimization harness injects its own __matmBootMark at
	// document-start (measure-boot.mjs addInitScript). If it's already here, that
	// context owns collection — do not overwrite it, and do not ship (the harness
	// reads window.__matmBootMarks itself).
	if (typeof window.__matmBootMark === 'function') return;

	const marks = [];
	window.__matmBootMarks = marks;

	// Whether to arm the collector at all (cheap gate, evaluated once). The
	// MATM-bundle check is deliberately NOT folded in here: at install time (top
	// of admin-app index.js) the bundle id is still the platform default — MATM's
	// App.js only sets it to com.meetatthemirror later, when it renders (via
	// useSetBundleAppId). So we defer the bundle check to finalize(), which runs
	// well after that render.
	const enabled = computeEnabled();

	let finalized = false;

	function finalize(terminal, heroPath, partial) {
		if (finalized) return;
		finalized = true;

		// Ship only for a bundle we have an event name for — the shared shim/
		// adminapp marks fire for every managed app, but the cold-boot event is
		// per-app. By now (deferred, post-render) getBundleAppId() reflects the
		// real app.
		const eventName = BOOT_PROFILE_APPS[getBundleAppId()];
		if (!eventName) return;

		// On timeout there's no terminal mark; fall back to the last mark's `now`
		// so total_ms reflects how far boot actually got.
		const lastMarkNow = marks.length
			? marks[marks.length - 1].now
			: null;
		const terminalNow = partial
			? lastMarkNow
			: markNow(marks, terminal);

		const phases = computeBootPhases(
			marks,
			readNavContext(),
			terminalNow,
		);
		const compactMarks = marks.map((m) => ({
			name: m.name,
			now: Math.round(m.now),
		}));

		const props = {
			hero_path: heroPath || 'unknown',
			terminal: partial ? 'timeout' : terminal,
			partial: !!partial,
			bundle_app_id: getBundleAppId(),
			native: (() => {
				try {
					return Capacitor.isNativePlatform();
				} catch {
					return false;
				}
			})(),
			platform: (() => {
				try {
					return Capacitor.getPlatform();
				} catch {
					return 'web';
				}
			})(),
			build_env: AppConfig.buildEnv,
			client_version: AppConfig.version,
			marks_count: marks.length,
			...phases,
			// Raw compact timeline for ad-hoc PostHog analysis / debugging.
			marks: compactMarks,
		};

		// Expose for manual inspection (DevTools, agent-browser, QA) even when
		// PostHog isn't present (web override, dev).
		window.__matmBootProfile = props;
		window.__bootProfile = props;
		console.info(`[boot-profile:${eventName}]`, JSON.stringify(props));

		// Reuse the existing PostHog pipeline. In prod `window.posthog` is the
		// queueing stub from posthogHelper.js until the real client swaps in, so
		// this capture is buffered and replayed FIFO after init — no loss. Rides
		// the registered super-props (appType, version, env, geo, ...).
		try {
			window.posthog?.capture?.(eventName, props);
		} catch (err) {
			console.warn(
				'[matm-boot-profile] posthog capture failed:',
				err,
			);
		}
	}

	// `__matmBootMark` is the historical name (MATM adopted this first) and is
	// kept verbatim so every existing call site keeps working. `__bootMark` is
	// the app-neutral alias new code should use — BC's marks read better under a
	// name that isn't another app's initials.
	const bootMark = (name, detail = {}) => {
		const mark = {
			name,
			detail,
			now: (() => {
				try {
					return performance.now();
				} catch {
					return 0;
				}
			})(),
			at: Date.now(),
		};
		marks.push(mark);

		// Deferred finalize on a terminal mark — never on the boot hot path.
		if (enabled && !finalized && TERMINAL_MARKS[name]) {
			const heroPath = TERMINAL_MARKS[name];
			later(() => finalize(name, heroPath, false));
		}

		return mark;
	};
	window.__matmBootMark = bootMark;
	window.__bootMark = bootMark;

	// Backstop finalize so a stalled/errored hero path still reports whatever we
	// captured. Deferred; the finalized guard makes it a no-op if a terminal mark
	// already shipped.
	if (enabled) {
		later(() => finalize(null, 'unknown', true), {
			delay: FINALIZE_TIMEOUT_MS,
		});
	}
}
